/**
 * RFC 4180 CSV of the cut list: CRLF line endings, every record the same width,
 * metadata rows first, then the parts with finished and cut sizes, material, stock and
 * glue-ups, then the glue-ups spelled out and the set-out datums. Reads only the BoardAssembly.
 */
import type { Fields, Part } from '@/domain/schemas';
import { fmtMm, fmtMm1, fmtNum } from '@/domain/units';
import { shortHash } from '@/domain/hash';
import { criticalBlocker } from '@/domain/fields';
import { ExportRefusedError, glueUpText, packMetadata, profileLine, type ExportBuild } from './common';

export const CSV_HEADER = [
  'Part ID', 'Role', 'Qty', 'Finished length (mm)', 'Finished width (mm)', 'Thickness (mm)',
  'Cut length (mm)', 'Cut width (mm)', 'Grain', 'Edge', 'Material', 'Stock', 'Glue-up',
] as const;

export interface CsvOptions {
  revisionId?: string | null;
  /** Overrides the Status row of a valid design (default "Draft"). */
  status?: string;
  generatedAt?: string;
  /** Field records: an unconfirmed or unresolved critical refuses the export. */
  fields?: Fields;
  /** Write the CSV even for an invalid/unconfirmed design, with a "do not cut" status. */
  force?: boolean;
  /** Legacy parts-only table (no metadata, no datums). */
  plain?: boolean;
}

/** One RFC 4180 cell: quoted when it contains a quote, comma, CR or LF; quotes doubled. */
export function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

const grainCsv = (p: Part) => (p.grainWorld === 'y' ? 'Along length (vertical)' : p.grainWorld === 'z' ? 'Along length (front to back)' : 'Along length (horizontal)');
const glueCsv = (p: Part) => (p.glueUp ? `${p.glueUp.strips} strips of ${fmtMm(p.glueUp.stripWidthTicks)}` : '');

/** "birch-ply-18: Birch plywood 18 mm, … (measured 18.0 mm); back Birch plywood 6 mm, … (measured 6.0 mm)". */
export function stockLine(build: Pick<ExportBuild, 'stocks'>): string {
  return build.stocks
    .map((s) => (s.id.endsWith(':back') ? `back ${s.label} (measured ${fmtMm1(s.thicknessTicks)} mm)` : `${s.id}: ${s.label} (measured ${fmtMm1(s.thicknessTicks)} mm)`))
    .join('; ');
}

/**
 * Cut list CSV. Refuses (throws ExportRefusedError) an invalid build or unconfirmed critical
 * field unless {force:true}, which writes `Status, INVALID — do not cut`.
 */
export function toCSV(build: ExportBuild, opts: CsvOptions = {}): string {
  const invalid = build.validation.ok === false;
  const unconfirmed = opts.fields ? criticalBlocker(opts.fields) : null;
  if ((invalid || unconfirmed) && !opts.force) {
    throw new ExportRefusedError(invalid ? `Invalid — not for cutting. ${build.validation.errors[0]?.message ?? ''}`.trim() : unconfirmed!);
  }
  const join = (rows: unknown[][]) => rows.map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';

  if (opts.plain) {
    const head = ['Part ID', 'Role', 'Qty', 'Length', 'Width', 'Thickness', 'Grain', 'Edge', 'Material'];
    const rows = build.parts.map((p) => [p.id, p.role, p.qty, fmtMm(p.finished.l), fmtMm(p.finished.w), fmtMm(p.finished.t), grainCsv(p), p.edge, p.material]);
    return join([head, ...rows]);
  }

  const width = CSV_HEADER.length;
  const meta = packMetadata(build, { revisionId: opts.revisionId ?? null, ...(opts.generatedAt ? { generatedAt: opts.generatedAt } : {}) });
  const status = invalid ? 'INVALID — do not cut' : unconfirmed ? 'UNCONFIRMED — do not cut' : (opts.status ?? 'Draft');
  const sameCut = build.parts.every((p) => p.cut.l === p.finished.l && p.cut.w === p.finished.w);
  const rec = (...cells: unknown[]) => {
    const r = cells.slice(0, width);
    while (r.length < width) r.push('');
    return r;
  };
  const glued = build.parts.filter((p) => p.glueUp);
  const rows: unknown[][] = [
    rec('Semblaro cut list'),
    rec('Revision', meta.revisionId || 'unsaved'),
    rec('Manufacturing hash', shortHash(meta.manufacturingHash)),
    rec('Status', status),
    rec('Units', 'mm'),
    rec('Generated', meta.generatedAt),
    rec('Stock', stockLine(build)),
    rec('Cut size', sameCut ? 'Equal to finished size for this profile: panels are cut to finished size on a panel saw.' : 'Includes the cut allowance of the construction profile.'),
    rec('Profile', profileLine(build)),
    rec('Appearance hash', shortHash(meta.appearanceHash)),
    rec('Generator', meta.generatorVersion),
    rec('Manufacturing hash (SHA-256)', meta.manufacturingHash),
    rec('Family', `${build.familyLabel} (${build.familyId} v${build.familyVersion})`),
    rec(),
    [...CSV_HEADER],
    ...build.parts.map((p) => rec(p.id, p.role, p.qty, fmtMm(p.finished.l), fmtMm(p.finished.w), fmtMm(p.finished.t), fmtMm(p.cut.l), fmtMm(p.cut.w), grainCsv(p), p.edge, p.material, p.stockId, glueCsv(p))),
    ...(glued.length ? [rec(), rec('Glue-ups (edge-glued from strips; L along the grain)'), ...glued.map((p) => rec(glueUpText(p)))] : []),
    rec(),
    rec('Shelf datums (underside from the floor)', 'mm'),
    ...build.datums.map((d) => rec(d.label, fmtNum(d.mm))),
  ];
  return join(rows);
}
